import React, { useEffect, useState } from 'react';
import { Drawer, Button, message } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import * as actions from "store/actions/AuthActions";
import useIsMobile from 'hooks/useIsMobile';

const AddContactsDrawer = ({ visible, onClose, campaignId, onSuccess, existingContactIds = [] }) => {
    const isMobile = useIsMobile();
    const [contacts, setContacts] = useState([]);
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (visible) {
            fetchContacts();
        }
    }, [visible]);

    const fetchContacts = async () => {
        setLoading(true);
        try {
            const response = await actions.loadContacts(1, '', MAX_PAGE_SIZE);
            if (response.status === 200) {
                // Filter out contacts that are already added to this campaign
                const allContacts = response.data.results || [];
                const filteredContacts = allContacts.filter(contact => !existingContactIds.includes(contact.id));
                setContacts(filteredContacts);
            }
        } catch (error) {
            message.error('Failed to load contacts');
        }
        setLoading(false);
    };

    const handleAddContacts = async () => {
        if (selectedContacts.length === 0) {
            message.warning('Please select at least one contact');
            return;
        }

        setSubmitting(true);
        try {
            for (let contactId of selectedContacts) {
                const data = {
                    campaign: campaignId,
                    contact: contactId,
                };
                await actions.addCampaignContact(data);
            }
            message.success(`${selectedContacts.length} contact(s) added successfully`);
            setSelectedContacts([]);
            onSuccess();
            onClose();
        } catch (error) {
            message.error('Failed to add contacts');
        }
        setSubmitting(false);
    };

    const columns = [
        {
            title: 'Name',
            key: 'name',
            render: (_, record) => `${record.first_name || ''} ${record.last_name || ''}`.trim(),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Phone',
            dataIndex: 'phone_number',
            key: 'phone_number',
        },
        {
            title: 'Mobile',
            dataIndex: 'mobile',
            key: 'mobile',
        },
    ];

    const rowSelection = {
        selectedRowKeys: selectedContacts,
        onChange: (selectedRowKeys) => {
            setSelectedContacts(selectedRowKeys);
        },
    };

    return (
        <Drawer
            title="Add Contacts to Campaign"
            placement={isMobile ? "bottom" : "right"}
            width={isMobile ? "100%" : 800}
            height={isMobile ? "90vh" : undefined}
            onClose={onClose}
            open={visible}
            styles={{
                body: { paddingBottom: isMobile ? 80 : 24 }
            }}
            footer={
                <div style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: '8px' }}>
                    <Button onClick={onClose} style={isMobile ? { flex: 1 } : undefined}>Cancel</Button>
                    <Button
                        type="primary"
                        onClick={handleAddContacts}
                        loading={submitting}
                        style={isMobile ? { flex: 1 } : undefined}
                    >
                        {isMobile ? "Add Contacts" : "Add Selected Contacts"}
                    </Button>
                </div>
            }
        >
            <Table
                rowSelection={{
                    type: 'checkbox',
                    ...rowSelection,
                }}
                columns={columns}
                dataSource={contacts}
                loading={loading}
                rowKey="id"
                pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                size="small"
            />
        </Drawer>
    );
};

export default AddContactsDrawer;
